import { Link } from "react-router"
import BlogImage from "#app/components/blog-image.tsx"
import { TypographyH2 } from "#app/components/ui/typography.tsx"
import { getBlogImgSrc } from "#app/utils/misc.tsx"

type BlogPost = {
	id: string
	title: string
	slug: string
	excerpt: string | null
	publishedAt: Date | string | null
	imageId: string | null
	images: { id: string; altText: string | null }[]
}

export default function LatestBlogPosts({ posts }: { posts: BlogPost[] }) {
	return (
		<section className="border-t border-border/40 py-16">
			<div className="mb-8 flex items-end justify-between">
				<TypographyH2>Desde nuestro blog</TypographyH2>
				<Link
					to="/blog"
					className="hidden text-sm font-medium text-primary transition-colors hover:text-primary/80 dark:text-primary-foreground sm:inline-flex sm:items-center sm:gap-1"
				>
					Ver todos los artículos
					<svg
						className="h-4 w-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M17 8l4 4m0 0l-4 4m4-4H3"
						/>
					</svg>
				</Link>
			</div>

			<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
				{posts.map(post => (
					<Link
						key={post.id}
						to={`/blog/${post.slug}`}
						className="group flex flex-col overflow-hidden rounded-xl bg-card shadow-sm transition-all duration-300 hover:shadow-md dark:bg-card/50"
					>
						<div className="relative aspect-[16/9] overflow-hidden bg-secondary dark:bg-secondary/20">
							{post.imageId ? (
								<BlogImage
									src={getBlogImgSrc(post.imageId)}
									alt={post.images[0]?.altText ?? post.title}
									className="transition-transform duration-500 group-hover:scale-105"
									sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
								/>
							) : (
								<div className="flex h-full items-center justify-center">
									<svg
										className="h-10 w-10 text-muted-foreground/30"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={1}
											d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
										/>
									</svg>
								</div>
							)}
						</div>

						<div className="flex flex-1 flex-col p-4">
							{post.publishedAt && (
								<time
									dateTime={new Date(post.publishedAt).toISOString()}
									className="mb-2 text-xs text-muted-foreground dark:text-foreground/60"
								>
									{new Date(post.publishedAt).toLocaleDateString("es-ES", {
										day: "numeric",
										month: "short",
										year: "numeric",
									})}
								</time>
							)}
							<h3 className="font-serif text-lg font-medium leading-snug text-foreground transition-colors group-hover:text-primary dark:group-hover:text-primary-foreground">
								{post.title}
							</h3>
							{post.excerpt && (
								<p className="mt-2 line-clamp-2 text-sm text-muted-foreground dark:text-foreground/70">
									{post.excerpt}
								</p>
							)}
						</div>
					</Link>
				))}
			</div>

			<div className="mt-6 text-center sm:hidden">
				<Link
					to="/blog"
					className="inline-flex items-center gap-1 text-sm font-medium text-primary transition-colors hover:text-primary/80 dark:text-primary-foreground"
				>
					Ver todos los artículos
					<svg
						className="h-4 w-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M17 8l4 4m0 0l-4 4m4-4H3"
						/>
					</svg>
				</Link>
			</div>
		</section>
	)
}
